/* eslint-disable react/jsx-props-no-spreading */
import React, { useRef, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import { getPartners, getPartnersFilters } from '../../services/partner.service';
import {
  getAppliedFilter, getFormattedGroupArray, getSRPSearchPayLoad, getSRPURLSearchParams,
} from '../../utils/filter.util';
import { PAGE_COUNT } from '../../constants/app-constants';
import SRPReducer, { initialState } from './srpReducer';
import { SRPStateInterface } from './srpInterface';
import ACTIONS_TYPE from './actionType';
import { pageViewEvent, searchResultAnalytics } from '../analyticsApi';
import { BASENAME } from '../constants';
import logger from '../logger';

const isSRPListingError = (error) => (!error || (error?.partnersError));
export const SRPContext = React.createContext(initialState);
const getInitialState = (store) => {
  const { srpErrors = {} } = store || {};
  const srpStore = store;
  const { filterError = true } = srpErrors || {};
  const isListingError = isSRPListingError(srpErrors);
  if (filterError) return initialState;
  srpStore.loading = false;
  srpStore.loading = !!isListingError;
  return srpStore;
};
export function SRPContextProvider({
  children, store, currentPath, navigateToUrl,
}) {
  const [state, dispatch] = useReducer(SRPReducer, getInitialState(store));
  const srpStateRef = useRef();
  srpStateRef.current = state;

  const getURLSearchParams = () => {
    const queryParam = new URLSearchParams(window.location.search);
    let searchData = srpStateRef?.current?.appliedFilter || state.appliedFilter;
    if (queryParam.size) {
      searchData = getSRPURLSearchParams(queryParam);
    }
    return { searchData, queryParam };
  };
  const getPageNumberSize = () => {
    const pageCount = srpStateRef.current.appliedFilter.pageCount || state.appliedFilter.pageCount;
    const pageNumber = srpStateRef.current.appliedFilter.pageNumber || state.appliedFilter.pageNumber;
    return { pageCount, pageNumber };
  };
  const getAppliedMapFilter = (filters) => {
    const { searchData } = getURLSearchParams();
    const { pageNumber, pageCount } = getPageNumberSize();
    const selectedFilter = getAppliedFilter({ filters, appliedFilter: { ...searchData, pageCount, pageNumber } });
    return selectedFilter;
  };
  const setAppliedFilter = async (data) => {
    await dispatch({ type: ACTIONS_TYPE.SET_APPLIED_FILTERS, payload: data });
  };
  const getSetSearchFilter = async (filters) => {
    const selectedFilter = getAppliedMapFilter(filters);
    await setAppliedFilter(selectedFilter);
  };

  const getTranslatedFilters = async (filters = {}) => {
    const textToTranslate = [];
    Object.keys(filters).forEach((category) => {
      if (category.toLowerCase() !== 'solutions') {
        filters[category].forEach((element) => {
          const { group, values, value } = element || {};
          if (group) {
            textToTranslate.push({
              source: group,
            });
          }
          if (Array.isArray(values)) {
            values.forEach((item) => {
              textToTranslate.push({
                source: item.value,
              });
            });
          }
          if (value) {
            textToTranslate.push({
              source: value,
            });
          }
        });
      }
    });
    dispatch({ type: ACTIONS_TYPE.SET_TRANSLATION_TEXT, payload: textToTranslate });
  };

  const setFilterResponse = (filtersData, filterStatus = {}) => {
    const { filters } = getFormattedGroupArray({ filters: filtersData || {}, filterStatus });
    getSetSearchFilter(filters);
    getTranslatedFilters(filters);
    dispatch({ type: ACTIONS_TYPE.GET_FILTERS, payload: filters });
  };

  const getTranslatedPartners = async (partners) => {
    const textToTranslate = [];
    partners.forEach((element) => {
      if (element.display_name) {
        textToTranslate.push({
          source: element.display_name,
        });
      }
      if (element.headline) {
        textToTranslate.push({
          source: element.headline,
        });
      }
    });
    dispatch({ type: ACTIONS_TYPE.SET_TRANSLATION_TEXT, payload: textToTranslate });
  };
  const fetchPartnersData = async ({
    search = '',
    query = '',
    isSearch = false,
    sortBy = state.sort_by,
  } = {}) => {
    let payload = `page_count=${PAGE_COUNT}&page_no=1&sort_by=${sortBy}`;
    let prevListData = [];
    dispatch({ type: ACTIONS_TYPE.SRP_LISTING_LOADING, payload: true });
    if (search) {
      prevListData = search?.pageNumber === 1 ? [] : srpStateRef.current?.listPartners?.partners;
      payload = getSRPSearchPayLoad(search, false, sortBy);
      const searchParam = getSRPSearchPayLoad(search, true);
      const queryParamAvailable = new URLSearchParams(window.location.search).size;
      if ((searchParam || queryParamAvailable) && !query) {
        navigateToUrl(`${BASENAME}${searchParam}`);
      }
    }
    try {
      const { data } = await getPartners(payload);
      const currentPayload = {
        pagination: data?.pagination,
        partners: [...prevListData, ...data?.partners],
      };
      if (data?.partners?.length) {
        getTranslatedPartners(data?.partners);
      }
      if (search) {
        searchResultAnalytics({ search, currentPayload, isSearch });
      }
      dispatch({
        type: ACTIONS_TYPE.GET_SRP_LISTING,
        payload: currentPayload || [],
      });
      dispatch({
        type: ACTIONS_TYPE.SRP_LISTING_LOADING,
        payload: false,
      });
    } catch (error) {
      dispatch({
        type: ACTIONS_TYPE.SRP_LISTING_LOADING,
        payload: false,
      });
    }
  };

  const fetchFiltersData = async () => {
    try {
      const { data } = await getPartnersFilters();
      setFilterResponse(data);
    } catch (error) { /* empty */ }
  };

  const resetFilter = async () => {
    await dispatch({ type: ACTIONS_TYPE.GET_SRP_LISTING, payload: [] });
    await setAppliedFilter(SRPStateInterface.appliedFilter);
    navigateToUrl(BASENAME);
    await fetchPartnersData();
  };

  const refetchingData = async () => {
    try {
      const { queryParam } = getURLSearchParams();
      const [{ data: filterData }] = await Promise.all([
        getPartnersFilters(),
      ]);
      const { filters } = getFormattedGroupArray({ filters: filterData || {}, filterStatus: {} });
      setFilterResponse(filterData, {});
      const selectedFilter = getAppliedMapFilter(filters);
      await fetchPartnersData({
        search: selectedFilter,
        query: queryParam,
      });
    } catch (err) {
      logger.debug('handle reset=true refetching:  ', err);
    }
  };

  useEffect(() => {
    const fetchAPi = async () => {
      pageViewEvent();
      const { srpErrors } = store || { srpErrors: { filterError: true, filterStatusError: true, partnersError: true } };
      const query = new URLSearchParams(window.location.search);
      if (state?.listPartners?.partners?.length && query.size && currentPath.includes('support/partners')) {
        refetchingData();
      }
      if (query.size && !srpErrors?.filterError && state?.filters && Object.keys(state.filters).length) {
        await getSetSearchFilter(state.filters);
      }
      if (state?.listPartners?.partners?.length) {
        getTranslatedPartners(state.listPartners.partners);
      }
      if (state?.filters && Object.keys(state?.filters)?.length) {
        getTranslatedFilters(state?.filters);
      }
      if (!srpErrors || srpErrors?.filterError) {
        await fetchFiltersData();
      }
      console.log(srpErrors, 'srpErrors--->>>');
      if (isSRPListingError(srpErrors)) {
        const { queryParam } = getURLSearchParams();
        const selectedFilter = getAppliedMapFilter(state.filters);
        await setAppliedFilter(selectedFilter);
        await fetchPartnersData({
          search: selectedFilter,
          query: queryParam,
        });
      }
    };
    fetchAPi();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SRPContext.Provider
      value={{
        filters: state.filters,
        listPartners: state.listPartners,
        loading: state.loading,
        partnerDetails: state.partnerDetails,
        fetchPartnersData,
        fetchFiltersData,
        getAppliedMapFilter,
        sortListBy: state.sort_by,
        appliedFilter: state.appliedFilter,
        setAppliedFilter,
        srpStateRef,
        resetFilter,
        dispatch,
        textToTranslate: state.textToTranslate,
      }}
    >
      {children}
    </SRPContext.Provider>
  );
}

SRPContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
  store: PropTypes.shape({}),
  currentPath: PropTypes.string,
  navigateToUrl: PropTypes.func.isRequired,
};
SRPContextProvider.defaultProps = {
  store: {},
  currentPath: '',
};

export const SRPContextConsumer = (Component) => (props) => (
  <SRPContext.Consumer>
    {(context) => <Component {...props} {...context} />}
  </SRPContext.Consumer>
);
